import { httpClient } from '../client';
import {
  Booking,
  BookingResponse,
  CreateBookingDTO,
  UpdateBookingDTO,
} from '../../../types/booking';
import { logger } from '../../../utils/logger';
import { BaseEndpoint } from './base.endpoint';

class BookingEndpoints extends BaseEndpoint {
  private static instance: BookingEndpoints;

  private constructor() {
    super('organisations');
  }

  static getInstance(): BookingEndpoints {
    if (!BookingEndpoints.instance) {
      BookingEndpoints.instance = new BookingEndpoints();
    }
    return BookingEndpoints.instance;
  }

  async listBookings(
    organizationId: string,
    startDate?: string,
    endDate?: string
  ): Promise<Booking[]> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<BookingResponse>(
        `${this.basePath}/${organizationId}/bookings?startDate=${startDate}&endDate=${endDate}`,
        { headers }
      );

      logger.info('Bookings fetched successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to fetch bookings:', error);
      return [];
    }
  }

  async listBookingsByResourceId(
    organizationId: string,
    resourceId: number,
    startDate?: string,
    endDate?: string
  ): Promise<{ bookings: Booking[]; externalEvents: any[] }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<BookingResponse>(
        `${this.basePath}/${organizationId}/bookings/resources/${resourceId}?startDate=${startDate}&endDate=${endDate}`,
        { headers }
      );

      logger.info('Bookings by resource ID fetched successfully');
      return {
        bookings: response.data.data,
        externalEvents: response.data.externalEvents,
      };
    } catch (error) {
      logger.error('Failed to fetch bookings by resource ID:', error);
      return { bookings: [], externalEvents: [] };
    }
  }

  async createBooking(organizationId: string, data: CreateBookingDTO): Promise<Booking> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<{ data: Booking }>(
        `${this.basePath}/${organizationId}/bookings`,
        data,
        { headers }
      );

      logger.info('Booking created successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to create booking:', error);
      throw error;
    }
  }

  async getBookingById(organizationId: string, bookingId: number): Promise<Booking> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<{ data: Booking }>(
        `${this.basePath}/${organizationId}/bookings/${bookingId}`,
        { headers }
      );

      logger.info('Booking fetched successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to fetch booking:', error);
      throw error;
    }
  }

  async updateBooking(
    organizationId: string,
    bookingId: number,
    data: UpdateBookingDTO
  ): Promise<Booking> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.put<{ data: Booking }>(
        `${this.basePath}/${organizationId}/bookings/${bookingId}`,
        data,
        { headers }
      );

      logger.info('Booking updated successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to update booking:', error);
      throw error;
    }
  }

  async deleteBooking(organizationId: string, bookingId: number): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(`${this.basePath}/${organizationId}/bookings/${bookingId}`, {
        headers,
      });

      logger.info('Booking deleted successfully');
    } catch (error) {
      logger.error('Failed to delete booking:', error);
      throw error;
    }
  }

  async deleteRecurrenceBooking(
    organizationId: string,
    recurrenceId: number,
    bookingId: number
  ): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(
        `${this.basePath}/${organizationId}/bookings/${bookingId}/recurrence/${recurrenceId}`,
        {
          headers,
        }
      );

      logger.info('Recurrence booking deleted successfully');
    } catch (error) {
      logger.error('Failed to delete recurrence booking:', error);
      throw error;
    }
  }
}

export const bookingEndpoints = BookingEndpoints.getInstance();
